import { forwardRef } from 'react'
import { QRCodeCanvas } from 'qrcode.react'
import { SenderRole } from '../../types/chat-model'
import type { SharePayload } from '../../types/share'
import { SHARE_CARD_THEME as t } from './share-theme'

interface SharePreviewCardProps {
  payload: SharePayload
}

/**
 * 分享卡片组件 - 被 modern-screenshot 截图的目标
 * 使用固定深色主题确保截图渲染一致性
 * 主题值来自 src/styles/theme.css
 */
export const SharePreviewCard = forwardRef<
  HTMLDivElement,
  SharePreviewCardProps
>(({ payload }, ref) => {
  const { engineTitle, messages, shareUrl } = payload

  return (
    <div
      ref={ref}
      style={{
        width: 400,
        minHeight: 300,
        backgroundColor: t.background,
        borderRadius: t.radiusLg,
        padding: t.spacing2xl,
        fontFamily: t.fontSans,
        color: t.foreground,
      }}
    >
      {/* 头部：引擎信息 */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: t.spacingMd,
          marginBottom: t.spacingXl,
          paddingBottom: t.spacingLg,
          borderBottom: `1px solid ${t.divider}`,
        }}
      >
        {/* {engineCoverUrl && (
          <img
            src={engineCoverUrl}
            alt=''
            crossOrigin='anonymous'
            style={{
              width: 48,
              height: 48,
              borderRadius: t.radiusMd,
              objectFit: 'cover',
            }}
          />
        )} */}
        <div>
          <div
            style={{
              fontSize: t.fontSizeLg,
              fontWeight: 600,
              marginBottom: 2,
              color: t.foreground,
            }}
          >
            {engineTitle}
          </div>
          <div style={{ fontSize: t.fontSizeBase, color: t.mutedForeground }}>
            对话片段 · {messages.length} 条消息
          </div>
        </div>
      </div>

      {/* 消息列表 */}
      <div
        style={{ display: 'flex', flexDirection: 'column', gap: t.spacingMd }}
      >
        {messages.map((msg, index) => (
          <MessageBubble key={msg.id || index} message={msg} />
        ))}
      </div>

      {/* 底部二维码区域 */}
      <div
        style={{
          marginTop: t.spacing2xl,
          paddingTop: t.spacingXl,
          borderTop: `1px solid ${t.divider}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <div>
          <div
            style={{
              fontSize: t.fontSizeMd,
              fontWeight: 500,
              marginBottom: t.spacing,
              color: t.foreground,
            }}
          >
            扫码开始你的对话
          </div>
          <div style={{ fontSize: t.fontSizeSm, color: t.mutedForeground }}>
            或访问链接体验完整引擎
          </div>
        </div>
        <div
          style={{
            backgroundColor: t.qrBackground,
            padding: 6,
            borderRadius: t.radiusSm,
          }}
        >
          <QRCodeCanvas
            value={shareUrl}
            size={72}
            level='M'
            bgColor={t.qrBackground}
            fgColor={t.qrForeground}
          />
        </div>
      </div>

      {/* 品牌 Logo */}
      <div
        style={{
          marginTop: t.spacingLg,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          opacity: 0.7,
        }}
      ></div>
    </div>
  )
})

SharePreviewCard.displayName = 'SharePreviewCard'

// 消息气泡组件
function MessageBubble({
  message,
}: {
  message: { id: string; role: SenderRole; content: string }
}) {
  const isUser = message.role === SenderRole.USER

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: isUser ? 'row-reverse' : 'row',
        alignItems: 'flex-start',
        gap: t.spacingSm,
      }}
    >
      {/* 消息内容 */}
      <div
        style={{
          maxWidth: '95%',
          backgroundColor: isUser ? t.userBubble : t.aiBubble,
          borderRadius: t.radiusMd,
          padding: `${t.spacingSm}px ${t.spacingMd}px`,
          fontSize: t.fontSizeMd,
          lineHeight: 1.6,
          color: t.foreground,
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
        }}
      >
        {simplifyContent(message.content)}
      </div>
    </div>
  )
}

/**
 * 简化内容：移除复杂 Markdown，保留可读性
 */
function simplifyContent(content: string): string {
  return (
    content
      // 移除代码块
      .replace(/```[\s\S]*?```/g, '[代码块]')
      // 移除图片
      .replace(/!\[.*?\]\(.*?\)/g, '[图片]')
      // 移除链接，保留文本
      .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
      // 移除 HTML 标签
      .replace(/<[^>]+>/g, '')
  )
}
